import { defineStore } from "pinia";

import { __subscription } from "~/stores/subscription.store";

import { fetchWithCache } from "~/utils/fetch";

import type { PBCollection } from "~/types/pb/collections";
import { type ClientData, ObjectStorageManageMode } from "~/types/pocketbase";
import { Sub } from "~/types/subscriptions";

interface clientsInterface {
	clients: Map<ClientData["id"], ClientData>;
	clientId: ClientData["id"] | null;
	clientsFetched: Map<ClientData["id"], boolean>;
	hasStartFetchProcess: boolean;
}

export const __clients = defineStore("clientsStore", {
	state: (): clientsInterface => ({
		clients: new Map(),
		clientId: null,
		clientsFetched: new Map(),
		hasStartFetchProcess: false
	}),
	getters: {
		/**
		 * Retrieves all clients as an array filtered by the current subscription.
		 *
		 * @returns {PBCustomer[]} An array of all clients.
		 */
		clientsArray (): ClientData[] {
			const isPartner = __subscription().has(Sub.Type.Partner);
			return Array.from(this.clients.values())
				.sort((a: ClientData, b: ClientData) => a.ref?.localeCompare(b.ref) ?? 0)
				.filter((client: ClientData) => client.mode === null || (client.mode && client.mode !== (isPartner ? ObjectStorageManageMode.Client : ObjectStorageManageMode.Partner)));
		},
		/**
		 * Retrieves all clients as an array.
		 *
		 * @returns {PBCustomer[]} An array of all clients.
		 */
		clientsArrayFull (): ClientData[] {
			return Array.from(this.clients.values())
				.sort((a: ClientData, b: ClientData) => a.ref?.localeCompare(b.ref) ?? 0);
		},

		/**
		 * Retrieves the client ID.
		 * Throws an error if no clients are available.
		 *
		 * @returns {string} The client ID.
		 */
		getClientId (): ClientData["id"] {
			// if has only 1 client, return its ID
			if (this.clients.size === 1
				&& this.clientsArray.length === 1
				&& !__subscription().has(Sub.Type.Partner)) {
				// noinspection JSPotentiallyInvalidTargetOfIndexedPropertyAccess
				return this.clientsArray[0].id;
			}

			// Si un client est sélectionné, retourner son ID
			if (this.clientId) {
				return this.clientId;
			}

			// S'il y a des clients disponibles, retourner le premier
			if (this.clientsArray.length > 0) {
				return this.clientsArray![0].id;
			}

			if (!(__subscription().has(Sub.Type.ObjectStorage) || __subscription().has(Sub.Type.Partner))) {
				// Si aucun client n'est sélectionné et qu'il n'y a pas de clients disponibles, retourner null
				return null;
			}

			// Aucun client disponible, lancer une erreur
			throw new Error("Aucun client disponible");
		},
		/**
		 * Retrieves the current team ID of the active client.
		 *
		 * @returns {string | null} The team ID of the current client, or null if not found.
		 */
		getCurrentTeamId (): string | null {
			const client = this.clients.get(this.clientId);
			return client?.teamId || null;
		},
		/**
		 * Retrieves the current client.
		 *
		 * @returns {PBCustomer | null} The current client.
		 */
		getCurrentClient (): ClientData | null {
			return this.clients.get(this.clientId || this.getClientId) || null;
		},
		/**
		 * Retrieves the count of clients.
		 *
		 * @returns {number} The count of clients.
		 */
		getClientCount (): number {
			return this.clients.size;
		},

		/**
		 * If has only one client, returns the client.
		 *
		 * @returns {PBCustomer | null} The client if there is only one, otherwise null.
		 */
		getOnlyClient (): ClientData | null {
			if (this.clients.size === 1 && this.clientsArray.length === 1) {
				return this.clientsArray![0];
			}
		},

		hasAllClientsInPartnerOnlyMode (): boolean {
			return this.clientsArrayFull.every(client => client.mode === ObjectStorageManageMode.Partner);
		},

		hasAllClientsInClientOnlyMode (): boolean {
			return this.clientsArrayFull.every(client => client.mode === ObjectStorageManageMode.Client);
		}
	},
	actions: {
		/**
		 * Adds a new client to the clients collection.
		 *
		 * @param {PBCustomer} newClient - The client to be added, identified by its unique ID.
		 * @return {void}
		 */
		addClient (newClient: ClientData): void {
			this.clients.set(newClient.id, newClient);
			void this.fetchAllowedUsage(newClient.id);
			__identifier().createMissingClient(newClient.id);
		},

		/**
		 * Updates an existing client with partial data.
		 * @param clientId - The ID of the client to be updated.
		 * @param updatedData - An object containing the updated data.
		 */
		updateClient (clientId: ClientData["id"], updatedData: Partial<ClientData>) {
			const client: ClientData | undefined = this.clients.get(clientId);
			if (client) {
				const updatedClient = { ...client, ...updatedData };
				this.clients.set(clientId, updatedClient);
			}
		},
		async fetchAllowedUsage (id: ClientData["id"]) {
			const client = this.clients.get(id);
			const ids = await PB.i.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.getFullList({
					filter: `subscription = "${id}"`,
					fetch: fetchWithCache
				});

			ids.forEach(id => {
				this.updateClient(id.subscription, { storage: (convertToToBytes(client.storage || 0)) });
			});
		},

		/**
		 * Sets the client ID.
		 *
		 * @param {string} clientId - The client ID to set.
		 * @return {void}
		 */
		setClientId (clientId: ClientData["id"]): void {
			this.clientId = clientId;
		},
		/**
		 * Retrieves a client by its ID.
		 *
		 * @param clientId - The ID of the client to retrieve.
		 */
		getClient (clientId: ClientData["id"]): ClientData | undefined {
			return this.clients.get(clientId);
		},

		/**
		 * Marque qu'un client a été chargé avec succès.
		 *
		 * @param {ClientData["id"]} clientId - L'ID du client qui a été chargé.
		 * @return {void}
		 */
		setClientsFetched (clientId: ClientData["id"]): void {
			this.clientsFetched.set(clientId, true);
		},

		/**
		 * Ajoute un client à la liste des clients à charger.
		 *
		 * @param {ClientData["id"]} clientId - L'ID du client à charger.
		 * @return {void}
		 */
		addClientToFetch (clientId: ClientData["id"]): void {
			this.hasStartFetchProcess = true;

			if (!this.clientsFetched.has(clientId)) {
				this.clientsFetched.set(clientId, false);
			}
		},

		/**
		 * Vérifie si tous les clients ont été chargés.
		 *
		 * @return {boolean} - True si tous les clients ont été chargés, false sinon.
		 */
		getIsFetch (): boolean {
			if (!this.hasStartFetchProcess) {
				// Si le processus de chargement n'a pas commencé, retourne true
				return false;
			}

			// Si aucun client n'est à charger, retourne true
			if (this.clientsFetched.size === 0) {
				return true;
			}

			// Vérifie que tous les clients ont été chargés
			for (const [ _, isFetched ] of this.clientsFetched.entries()) {
				if (!isFetched) {
					return false;
				}
			}

			return true;
		}
	}
});
